import { ColDef, RowSelectedEvent } from '@ag-grid-community/core';
import { Box, Flex, useBreakpointValue, VStack, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddressDto, PharmacyDto } from '../../../lib/ServerApi';
import { api } from '../../../lib/api';
import { LinkButton } from '@/components/ui/link-button';
import { Grid } from '@/components/ui/grid';
import { Address } from '@/components/ui/address';
import { formatPhone } from '@/components/ui/phone';

const defaultColumnDef: ColDef<PharmacyDto> = {
    flex: 1,
    resizable: true,
    floatingFilter: false,
};

export function PharmaciesPage() {
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const query = useQuery<PharmacyDto[]>({
        queryKey: ['pharmacy', 'list'],
        queryFn: async () => {
            const { data } = await api.pharmacy.list();
            return data;
        },
    });

    const colDefs = useMemo<ColDef<PharmacyDto>[]>(() => {
        if (isMobile) {
            return [
                {
                    field: 'name',
                    headerName: 'Pharmacy',
                    autoHeight: true,
                    cellRenderer: ({ data }: { data: PharmacyDto }) => {
                        const addressText = data.address
                            ? `${data.address.city ? data.address.city : ''}${data.address.state ? `, ${data.address.state}` : ''}`.trim()
                            : 'No address';

                        return (
                            <VStack align="start" py="3" gap="1">
                                <Text fontWeight="medium" fontSize="sm" lineHeight="short" overflowX="auto">
                                    {data.name || 'No name'}
                                </Text>
                                <Text fontSize="xs" color="gray.600" lineHeight="short">
                                    {addressText}
                                </Text>
                                <Text fontSize="xs" color="gray.600" lineHeight="short">
                                    {formatPhone(data.phone)}
                                </Text>
                            </VStack>
                        );
                    },
                },
            ];
        }

        return [
            {
                field: 'name',
                headerName: 'Pharmacy',
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: true,
                resizable: false,
                maxWidth: 300,
                lockPinned: true,
                pinned: 'left',
            },
            {
                field: 'address',
                headerName: 'Address',
                filter: 'agTextColumnFilter',
                maxWidth: 500,
                wrapText: true,
                suppressHeaderFilterButton: true,
                resizable: true,
                cellRenderer: ({ value }: { value: AddressDto | null }) => {
                    return value ? <Address address={value} /> : '';
                },
            },
            {
                field: 'phone',
                headerName: 'Phone',
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: true,
                resizable: false,
                flex: 1,
                valueFormatter: ({ value }) => {
                    if (!value) {
                        return 'n/a';
                    }

                    return formatPhone(value) || 'uk';
                },
            },
        ];
    }, [isMobile]);

    const handleSelect = (event: RowSelectedEvent) => {
        if (event.node.isSelected()) {
            navigate(`/settings/pharmacies/${event.data?.id}/edit`);
        }
    };

    return (
        <Flex px={{ base: '16px', md: 0 }} h="100%" flexDirection="column">
            <Flex alignItems="center" justifyContent="space-between" pb="12px">
                <Flex>
                    <LinkButton size="sm" to="/settings/pharmacy/create" textDecoration="none">
                        Add Pharmacy
                    </LinkButton>
                </Flex>
            </Flex>
            <Box position="relative" w="100%" h="100%" pb="24px">
                <Grid
                    key={isMobile ? 'mobile-grid' : 'grid'}
                    rowSelection="single"
                    columnDefs={colDefs}
                    rowData={query.data}
                    suppressCellFocus
                    defaultColDef={defaultColumnDef}
                    autoSizeStrategy={isMobile ? { type: 'fitCellContents' } : { type: 'fitGridWidth' }}
                    getRowHeight={isMobile ? () => 60 : undefined}
                    onRowSelected={handleSelect}
                />
            </Box>
        </Flex>
    );
}
